import React, { useState } from 'react'

// Components
import { Dialog } from '../components/Dialog'

// Service
import { createTransaction } from '../services/purchases.service'

/**
 * usePurchaseTransaction
 *
 * Owns submitting a purchase, together with the confirmation the API asks for when one
 * looks like a repeat of a purchase made moments ago (EXEC-1605).
 *
 * Authorize.Net used to refuse a second identical payment on the same card within two
 * minutes, with a message the buyer could do nothing with. Each attempt now carries its
 * own invoice number, so the gateway no longer stands in the way — which leaves the
 * question of whether a repeat was intended, and only the buyer can answer that. This
 * puts it to them once, with the amount spelled out, rather than either charging twice
 * silently or blocking a real second order.
 *
 * Every purchase flow uses this, so the answer and the wording are the same wherever the
 * purchase was started from.
 *
 * @returns {object} `loadingPurchase` for the purchase modal, `submitPurchase` to charge
 *  the card, and `repeatPurchaseDialog` to render alongside it.
 */
const usePurchaseTransaction = () => {
  // State
  const [loadingPurchase, setLoadingPurchase] = useState(false)
  const [repeatPurchase, setRepeatPurchase] = useState(null)

  /**
   * Charge the card for a purchase.
   * @param {string} eventId
   * @param {string} eventExhibitorId
   * @param {object} chargeData payload from `getChargeData`
   * @param {func} onError called with a message when the purchase fails
   * @param {func} onSuccess called once the purchase goes through
   */
  const submitPurchase = async (eventId, eventExhibitorId, chargeData, onError, onSuccess) =>
    createTransaction(
      eventId,
      eventExhibitorId,
      chargeData,
      onError,
      setLoadingPurchase,
      onSuccess,
      (message) =>
        setRepeatPurchase({
          message,
          // Confirming resubmits the same purchase, this time saying it was meant
          confirm: () =>
            submitPurchase(
              eventId,
              eventExhibitorId,
              { ...chargeData, confirm_duplicate: true },
              onError,
              onSuccess,
            ),
        }),
    )

  const repeatPurchaseDialog = repeatPurchase ? (
    <Dialog
      loading={loadingPurchase}
      message={repeatPurchase.message}
      onCancel={() => setRepeatPurchase(null)}
      onConfirm={() => {
        const { confirm } = repeatPurchase
        setRepeatPurchase(null)
        confirm()
      }}
      title="Purchase this again?"
      type="warning"
    />
  ) : null

  return { loadingPurchase, submitPurchase, repeatPurchaseDialog }
}

export default usePurchaseTransaction
